import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import WordSquare from './WordSquare'
import Helpers from "@/Helpers";
import { resolveSectionRatingInput } from "@/sections/rankInput";

type WordSquarePage = { id?: number | string; page?: number; words?: unknown; [key: string]: unknown };

export default function WordSquares(props) {
   const { sectionData, values } = props;
   const currentSection = sectionData?.currentSection;
   const firstInputRef = useRef(null);
   const content: Record<string, unknown> = useMemo(
      () => Helpers.getSectionContent(currentSection, sectionData?.lang),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [currentSection?.contents, sectionData?.lang]
   );
   const elements = useMemo(() => Helpers.getSectionElements(content), [content]);
   const ratingInput = useMemo(() => resolveSectionRatingInput(elements), [elements]);
   const contentElements = useMemo(
      () => (Array.isArray(elements?.items) ? elements.items as WordSquarePage[] : []),
      [elements]
   );
   const contentWithOops = useMemo(
      () => ({ ...content, oops: elements?.oops || {} }),
      [content, elements]
   );
   const pageValues = contentElements.filter(p => p.page === Number(sectionData?.page || 1));
   const instructionsHtml = Helpers.getSectionHtmlValue(content, "instructions");
   const instructionsSecondaryHtml = Helpers.getSectionHtmlValue(content, "instructionsSecondary");

   function toHtmlString(value) {
      if (typeof value === "string") return value;
      if (value === null || value === undefined) return "";
      return String(value);
   }

   useEffect(() => {
      sectionData.registerSection( 300);
      // Use setTimeout to ensure the DOM has rendered before focusing
      // setTimeout(() => {
      if (firstInputRef.current) {
         firstInputRef.current.focus();
      }
      // }, 0);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentSection?.name]);

   useEffect(() => {
      if (firstInputRef.current) {
         firstInputRef.current.focus();
      }
   }, [sectionData?.page]);

   const onChange = useCallback(function (childId, value) {
      if (value === "" || value === null || value === undefined) {
         sectionData.onAnswer(childId, "");
         return;
      }
      const parsed = typeof value === "number" ? value : parseInt(String(value), 10);
      sectionData.onAnswer(childId, Number.isFinite(parsed) && parsed >= 1 && parsed <= 4 ? parsed : "");
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [sectionData?.onAnswer]);

   useEffect(() => {
      if (!sectionData?.isAdmin) return;

      const handleDocumentKeyDown = (e) => {
         const activeTag = document.activeElement?.tagName?.toLowerCase();
         const isInputFocused = activeTag === "input" || activeTag === "textarea" || activeTag === "select";

         // Keep existing per-input keyboard handling when an input has focus.
         if (isInputFocused) return;

         if (typeof sectionData?.onKeyDown === "function") {
            sectionData.onKeyDown(e);
         }

         if (e.key === "Enter") {
            e.preventDefault();
            const currentPage = Number(sectionData?.page || 1);
            const totalPages = contentElements.length > 0
               ? Math.max(...contentElements.map(m => Number((m as { page?: string | number })?.page || 1)))
               : 1;
            if (currentPage < totalPages && sectionData.onNextPage)
               sectionData.onNextPage();
            return;
         }

         if (!['1', '2', '3', '4'].includes(e.key)) return;

         const nextEmptyItem = pageValues.find((item) => {
            const fieldId = currentSection.formId + item.id;
            const hasValue = values[fieldId] && values[fieldId] !== "";
            return !hasValue;
         });

         if (!nextEmptyItem) return;

         e.preventDefault();
         onChange(nextEmptyItem.id, e.key);

         // Focus first empty input after state settles so keyboard flow continues.
         setTimeout(() => {
            for (let i = 0; i < pageValues.length; i++) {
               const field = document.querySelector(`input[tabindex="${i + 1}"], select[tabindex="${i + 1}"]`) as HTMLInputElement | HTMLSelectElement | null;
               const fieldId = currentSection.formId + pageValues[i].id;
               const hasValue = values[fieldId] && values[fieldId] !== "";
               if (field && !String(field.value ?? "").trim() && !hasValue) {
                  field.focus();
                  if ("select" in field && typeof field.select === "function") {
                     field.select();
                  }
                  return;
               }
            }
         }, 50);
      };

      document.addEventListener("keydown", handleDocumentKeyDown);
      return () => document.removeEventListener("keydown", handleDocumentKeyDown);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [contentElements, currentSection?.formId, onChange, pageValues, sectionData?.isAdmin, sectionData?.onKeyDown, sectionData?.onNextPage, sectionData?.page, values]);

   const [duplicates, updateDuplicates] = useState([]);

   // Handle key press events
   const handleKeyDown = (e) => {
      if (props?.sectionData?.onKeyDown) {
         props.sectionData.onKeyDown(e);
      }
      if (props?.sectionData?.isAdmin) {
         if (e.key === 'Enter') {
            // Let the parent component handle validation and decide whether to advance
            sectionData.onNextPage();
         }
         else {
            if (['1', '2', '3', '4'].includes(e.key)) {
               // Find the current input index
               const currentTarget = e.currentTarget;
               const currentIndex = parseInt(currentTarget.tabIndex) - 1; // tabIndex is 1-based

               // Allow the normal onChange to handle the value setting
               setTimeout(() => {
                  // After the value is set, advance to next empty input
                  const pageValues = contentElements.filter(p => p.page === Number(sectionData?.page || 1));

                  // Find the next empty input starting from the current position
                  let nextEmptyIndex = -1;
                  for (let i = currentIndex + 1; i < pageValues.length; i++) {
                     const nextField = document.querySelector(`input[tabindex="${i + 1}"], select[tabindex="${i + 1}"]`) as HTMLInputElement | HTMLSelectElement | null;
                     if (nextField) {
                        const fieldId = currentSection.formId + pageValues[i].id;
                        const hasValue = values[fieldId] && values[fieldId] !== "";

                        if (!String(nextField.value ?? "").trim() && !hasValue) {
                           nextEmptyIndex = i;
                           break;
                        }
                     }
                  }

                  if (nextEmptyIndex >= 0) {
                     const nextField = document.querySelector(`input[tabindex="${nextEmptyIndex + 1}"], select[tabindex="${nextEmptyIndex + 1}"]`) as HTMLInputElement | HTMLSelectElement | null;
                     if (nextField) {
                        nextField.focus();
                        if ("select" in nextField && typeof nextField.select === "function") {
                           nextField.select();
                        }
                     }
                  }
               }, 50); // Increased delay to ensure onChange and state updates complete
            }
         }
      }
   }

   const getIssueCountForPage = useCallback(function (validationValues, page) {
      // Check if page is provided
      if (!page) {
         console.warn("No page specified for validation");
         return 0;
      }

      // Get all elements for the current page
      const localPageValues = contentElements.filter(pv => pv.page === page);

      // Count total issues - start with 0
      let totalIssueCount = 0;

      // Track duplicates
      const valueMap = new Map();
      const duplicateIds = [];

      // First pass: find duplicates and missing values
      localPageValues.forEach(item => {
         const value = validationValues[currentSection.formId + item.id];

         // Check for missing values (count as an issue)
         if (value === undefined || value <= 0) {
            totalIssueCount++;
         }
         // Check for duplicates
         else {
            if (valueMap.has(value)) {
               // This is a duplicate - mark both this and the original as issues
               const originalId = valueMap.get(value);

               // Add to duplicates list if not already there
               if (!duplicateIds.includes(item.id)) {
                  duplicateIds.push(item.id);
               }
               if (!duplicateIds.includes(originalId)) {
                  duplicateIds.push(originalId);
               }
            } else {
               // First occurrence of this value
               valueMap.set(value, item.id);
            }
         }
      });

      // Add the count of duplicate items
      totalIssueCount += duplicateIds.length;

      return totalIssueCount;
   }, [contentElements, currentSection?.formId]);

   useEffect(() => {
      const page = Number(sectionData?.page || 1);
      const localPageValues = contentElements.filter((pv) => Number(pv?.page) === page);
      const valueMap = new Map();
      const duplicateIds = [];

      localPageValues.forEach((item) => {
         const value = values?.[currentSection.formId + item.id];
         if (value === undefined || value <= 0) return;

         if (valueMap.has(value)) {
            const originalId = valueMap.get(value);
            if (!duplicateIds.includes(item.id)) duplicateIds.push(item.id);
            if (!duplicateIds.includes(originalId)) duplicateIds.push(originalId);
         } else {
            valueMap.set(value, item.id);
         }
      });

      updateDuplicates(duplicateIds);
   }, [contentElements, currentSection?.formId, sectionData?.page, values]);

   useEffect(() => {
      const nextCount = Number(sectionData?.nextCount || 0);
      const page = Number(sectionData?.page || 1);
      const issueCount = nextCount > 0 ? getIssueCountForPage(values || {}, page) : 0;
      sectionData?.updateIssueCount?.(issueCount);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [contentElements, currentSection?.formId, getIssueCountForPage, sectionData?.nextCount, sectionData?.page, sectionData?.updateIssueCount, values]);

   return (
      <div className={`WordSquares sectionContent ${sectionData.showValidations ? 'showValidations' : ''}`}>
         {Number(sectionData?.page || 1) > 1 ?
            (<div className="instructions reminder">{parseSanitizedHtml(toHtmlString(instructionsHtml))}</div>) :
            (<div className="instructions">{parseSanitizedHtml(toHtmlString(instructionsSecondaryHtml))}</div>)
         }

         <div className="content center">
            {pageValues.map((p, i) => {
               const squareId = Number(p.id) || 0;
               const squareData = {
                  id: squareId,
                  words: Array.isArray(p.words) ? p.words : [],
               };
               const isValid = !duplicates.includes(squareId) || !values[currentSection.formId + squareId];
               return (
                  <WordSquare
                     data={squareData}
                     sectionData={sectionData}
                     id={squareId}
                     index={i}
                     duplicate={duplicates.includes(squareId)}
                     key={`wordsquare-${squareId}`}
                     showValidations={sectionData.showValidations}
                     setValue={onChange}
                     value={values[currentSection.formId + squareId] ?? ""}
                     lang={sectionData.lang}
                     isValid={isValid}
                     tabIndex={i + 1}
                     ref={i === 0 ? firstInputRef : null}
                     onKeyDown={handleKeyDown}
                     content={contentWithOops}
                     ratingInput={ratingInput}
                  />
               );
            })}
         </div>
      </div>
   )
}



